import { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import { supabase, LogEntry } from '../lib/supabase';
import { dataManager } from '../lib/dataManager';
import { generateUUID } from '../lib/uuid';
import { Save, Loader2, Plus, ExternalLink, Trash2, Repeat, ChevronDown, ChevronUp, Play, Clock } from 'lucide-react';
import { ImageUpload, ImageUploadHandle } from '../components/ui/ImageUpload';
import { SparePartFormV2, SparePartFormHandle } from './SparePartFormV2';
import type { SparePartFormData } from '../types/spareParts';
import { LocationSelectorModal } from '../components/cars/LocationSelectorModal';
import { LocationItem } from '../components/cars/LocationItem';
import { LogEntryItem } from '../components/cars/LogEntryItem';
import { LocationForm } from './LocationForm';
import { Location } from '../lib/supabase';
import { useQueryClient } from '@tanstack/react-query';
import { CAR_KEYS, LOG_ENTRY_KEYS, GALLERY_KEYS, LOCATION_KEYS, useLocations } from '../hooks/useCarData';
import { NavigationBar } from '../components/ui/NavigationBar';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { AlertModal } from '../components/ui/AlertModal';
import { useCarTasks } from '../hooks/useCarTasks';
import { CheckCircle2 } from 'lucide-react';
import { usePreferences } from '../contexts/PreferencesContext';
import { ExpenseLineItem, ExpenseLineItemData } from '../components/forms/ExpenseLineItem';
import { getExpenseTypeNameFromData, SupportedLanguage, isServiceType, normalizeExpenseTypeKey } from '../lib/expenseTypeHelpers';
import { useTranslations } from '../i18n';
import defaultExpenseTypesData from '../data/defaultExpenseTypes.json';
import planLimitsData from '../data/planLimits.json';
import { theme } from '../lib/theme';
import { deleteTempFiles } from '../lib/r2';
import { trackUserActivity } from '../lib/activityTracking';
import { DateInput } from '../components/ui/DateInput';
import { FormLabel } from '../components/ui/FormLabel';
import { convertToUTC, getDateInTimezone, getTimeInTimezone } from '../lib/timeUtils';
import { syncAllCarTaskReminders } from '../lib/taskReminders';

type AddRecordFormProps = {
  carId: string;
  onClose: () => void;
  onExpenseAdded: (entry: LogEntry) => void;
  logEntry?: LogEntry;
  isReadOnly?: boolean;
  mode?: 'add' | 'edit';
  allLogEntries?: LogEntry[];
  selectedCarOverride?: any;
  isDemo?: boolean;
};

export function AddRecordForm({
  carId, onClose, onExpenseAdded, logEntry, mode = 'add', isReadOnly = false,
  selectedCarOverride
}: AddRecordFormProps) {
  const { t, language } = useTranslations();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { tasks } = useCarTasks(carId);
  const [completedTaskIds, setCompletedTaskIds] = useState<string[]>([]);
  const { preferences } = usePreferences();
  const [expenseId] = useState(() => logEntry?.id || generateUUID());
  const outletContext = useOutletContext<any>() || {};
  const { effectiveUserId, ownerPreferences, selectedCar: contextSelectedCar, isAdminView } = outletContext;
  const selectedCar = selectedCarOverride || contextSelectedCar;

  // Determine available expense types for this car
  const availableExpenseTypes = selectedCar?.expense_types && selectedCar.expense_types.length > 0
    ? selectedCar.expense_types.map((key: string) => {
      const normalizedKey = normalizeExpenseTypeKey(key);
      const typeData = (defaultExpenseTypesData as any)[normalizedKey];
      return typeData ? { ...typeData, type_key: normalizedKey } : null;
    }).filter(Boolean)
    : Object.entries(defaultExpenseTypesData)
      .filter(([_, val]: [string, any]) => val.is_default !== false && !val.is_deprecated)
      .sort(([, a]: [string, any], [, b]: [string, any]) => (a.sort_order || 0) - (b.sort_order || 0))
      .map(([key, val]: [string, any]) => ({ ...val, type_key: key }));

  const [lineItems, setLineItems] = useState<ExpenseLineItemData[]>(() => {
    if (mode === 'edit' && logEntry?.expenses && logEntry.expenses.length > 0) {
      return logEntry.expenses.map(exp => ({
        id: exp.id,
        type: exp.type || '',
        title: exp.title || '',
        amount: exp.amount?.toString() || '',
        isSplitServiceCosts: (exp.amount_parts || 0) > 0 || (exp.amount_work || 0) > 0,
        amountParts: exp.amount_parts?.toString() || '',
        amountWork: exp.amount_work?.toString() || '',
        fuelLiters: exp.fuel_liters?.toString() || '',
        electricityKwh: exp.electricity_kwh?.toString() || '',
        fuelType: exp.fuel_type || '',
        description: exp.description || ''
      }));
    }
    return [{
      id: generateUUID(),
      type: '',
      title: '',
      amount: '',
      isSplitServiceCosts: false,
      amountParts: '',
      amountWork: '',
      fuelLiters: '',
      electricityKwh: '',
      fuelType: '',
      description: ''
    }];
  });

  const handleRemoveLocation = () => {
    setLocationId(null);
    setSelectedLocationObj(null);
  };

  const handleLineItemChange = (id: string, field: keyof ExpenseLineItemData, value: any) => {
    setLineItems(prev => prev.map(item => {
      if (item.id !== id) return item;
      const updated = { ...item, [field]: value };

      if (field === 'amount' && firstExpense?.is_initial_purchase) {
        setValueAtExpense(value);
      }
      return updated;
    }));
  };

  const handleValueChange = (val: string) => {
    setValueAtExpense(val);
    if (firstExpense?.is_initial_purchase) {
      if (lineItems.length > 0) {
        handleLineItemChange(lineItems[0].id, 'amount', val);
      }
    }
  };

  const handleAddLineItem = () => {
    setLineItems(prev => [...prev, {
      id: generateUUID(),
      type: '', title: '', amount: '', isSplitServiceCosts: false, amountParts: '', amountWork: '', fuelLiters: '', electricityKwh: '', fuelType: '', description: ''
    }]);
  };

  const handleRemoveLineItem = (id: string) => {
    setLineItems(prev => prev.filter(item => item.id !== id));
  };

  const [isFormInitialized, setIsFormInitialized] = useState(mode === 'add');
  const [title, setTitle] = useState(logEntry?.title || '');
  const [date, setDate] = useState(() => {
    if (logEntry?.date) return getDateInTimezone(logEntry.date, preferences?.timezone);
    return getDateInTimezone(new Date().toISOString(), preferences?.timezone);
  });

  const getInitialHasTime = (entry?: LogEntry) => {
    if (!entry) return false;
    // Use the explicit flag if it exists
    if (entry.time_used !== undefined && entry.time_used !== null) {
      return entry.time_used;
    }
    
    // Fallback to legacy guessing logic for old records
    const dStr = entry.date;
    if (!dStr) return false;
    if (!dStr.includes('T')) return false;
    if (dStr.endsWith('T00:00:00.000Z') || dStr.endsWith('T00:00:00Z')) return false;
    
    // If it's precisely 12:00 in the target timezone, we treat it as "no time" (our default)
    const timeInTz = getTimeInTimezone(dStr, preferences?.timezone);
    return timeInTz !== '12:00';
  };
  const [isTimeEnabled, setIsTimeEnabled] = useState(() => getInitialHasTime(logEntry));
  const [time, setTime] = useState(() => {
    if (getInitialHasTime(logEntry)) {
      return getTimeInTimezone(logEntry!.date, preferences?.timezone);
    }
    return getTimeInTimezone(new Date().toISOString(), preferences?.timezone);
  });

  const [mileageAtExpense, setMileageAtExpense] = useState(logEntry?.mileage && logEntry.mileage !== 0 ? logEntry.mileage.toString() : '');
  // Using logEntry.car_value as the primary source of truth, fallback to first expense
  const firstExpense = logEntry?.expenses && logEntry.expenses.length > 0 ? logEntry.expenses[0] : null;
  const [valueAtExpense, setValueAtExpense] = useState(logEntry?.car_value?.toString() || '');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [images, setImages] = useState<Array<{ url: string; thumbnail?: string; comment?: string; sizeBytes?: number; is_receipt?: boolean }>>([]);
  const [spareParts, setSpareParts] = useState<SparePartFormData[]>([]);
  const [locationId, setLocationId] = useState<string | null>(logEntry?.location_id || null);
  const [selectedLocationObj, setSelectedLocationObj] = useState<Location | null>(null);
  const [initialLocationId, setInitialLocationId] = useState<string | null>(null);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [isLocationCreateModalOpen, setIsLocationCreateModalOpen] = useState(false);

  const effectivePreferences = (ownerPreferences || preferences) ? {
    ...(ownerPreferences || preferences),
    distance_unit: selectedCar?.distance_unit || (ownerPreferences || preferences).distance_unit,
    volume_unit: selectedCar?.volume_unit || (ownerPreferences || preferences).volume_unit,
    currency: selectedCar?.currency || (ownerPreferences || preferences).currency,
    currency_symbol: selectedCar?.currency_symbol || (ownerPreferences || preferences).currency_symbol
  } : null;
  const currentLanguage = language as SupportedLanguage;
  
  // Repeat state
  const [isRepeating, setIsRepeating] = useState(false);
  const [repeatIntervalType, setRepeatIntervalType] = useState<'month' | 'day'>('month');
  const [repeatIntervalValue, setRepeatIntervalValue] = useState(1);
  const [repeatEndDate, setRepeatEndDate] = useState<string | null>(null);
  const [repeatCopyTitle, setRepeatCopyTitle] = useState(true);
  const [repeatCopyLocation, setRepeatCopyLocation] = useState(true);
  const [repeatCopyDescription, setRepeatCopyDescription] = useState(true);
  const [hasLoadedRepeat, setHasLoadedRepeat] = useState(false);
  const [initialRepeatConfig, setInitialRepeatConfig] = useState<any>(null);
  const [relatedEntries, setRelatedEntries] = useState<LogEntry[]>([]);
  const [isRelatedEntriesExpanded, setIsRelatedEntriesExpanded] = useState(false);
  const [showDeleteRepeatModal, setShowDeleteRepeatModal] = useState(false);
  const [hasEndDate, setHasEndDate] = useState(false);

  const effectiveCarId = carId || logEntry?.car_id || selectedCar?.id;
  const { data: allLocations = [] } = useLocations(effectiveCarId);
  const enrichedLocation = useMemo(() => {
    if (!selectedLocationObj) return null;
    const found = allLocations.find(l => String(l.id) === String(selectedLocationObj.id));
    const baseLoc = found || selectedLocationObj;
    
    // If the current log entry is temporary (or new) and linked to this location, 
    // it won't be counted in useLocations (which filters by is_temporary: false).
    // We add 1 manually to reflect that this record WILL use this location.
    const isThisEntryUsingThisLoc = (logEntry?.is_temporary || mode === 'add') && locationId === baseLoc.id;
    const baseCount = baseLoc.entryCount || baseLoc.entry_count || 0;
    
    return {
      ...baseLoc,
      entryCount: isThisEntryUsingThisLoc ? baseCount + 1 : baseCount
    };
  }, [selectedLocationObj, allLocations, logEntry, mode, locationId]);

  useEffect(() => {
    if (mode === 'edit' && repeatEndDate) {
      setHasEndDate(true);
    }
  }, [mode, repeatEndDate]);

  useEffect(() => {
    async function loadRepeatConfig() {
      if (!logEntry?.repeat_id || hasLoadedRepeat) return;
      
      const { data, error } = await supabase
        .from('log_entry_repeats')
        .select('*')
        .eq('id', logEntry.repeat_id)
        .single();
        
      if (data && !error) {
        setIsRepeating(true);
        setRepeatIntervalType(data.interval_type as any);
        setRepeatIntervalValue(data.interval_value);
        setRepeatEndDate(data.end_date);
        setRepeatCopyTitle(data.copy_title);
        setRepeatCopyLocation(data.copy_location);
        setRepeatCopyDescription(data.copy_description);
        setInitialRepeatConfig(data);
      }
      setHasLoadedRepeat(true);
    }
    
    if (mode === 'edit' && logEntry?.repeat_id) {
      loadRepeatConfig();
    }
  }, [logEntry?.repeat_id, mode, hasLoadedRepeat]);

  useEffect(() => {
    async function fetchRelatedEntries() {
      if (!logEntry?.repeat_id) return;
      
      const { data, error } = await supabase
        .from('log_entries')
        .select('*, expenses(*)')
        .eq('repeat_id', logEntry.repeat_id)
        .order('date', { ascending: false });
        
      if (data && !error) {
        setRelatedEntries(data);
      }
    }
    
    if (mode === 'edit' && logEntry?.repeat_id) {
      fetchRelatedEntries();
    }
  }, [logEntry?.repeat_id, mode]);

  const handleDeleteRepeatCall = async () => {
    if (!logEntry?.repeat_id) return;
    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('log_entry_repeats')
        .delete()
        .eq('id', logEntry.repeat_id);
        
      if (error) throw error;
      
      // Also nullify references in log_entries
      await supabase
        .from('log_entries')
        .update({ repeat_id: null })
        .eq('repeat_id', logEntry.repeat_id);
      
      setIsRepeating(false);
      setRelatedEntries([]);
      setShowDeleteRepeatModal(false);
      
      dataManager.invalidateLogEntries(carId);
      if (logEntry) {
        onExpenseAdded({ ...logEntry, repeat_id: null } as LogEntry);
      }
      onClose();
    } catch (error) {
      console.error('Error deleting repeat:', error);
      setAlertConfig({ isOpen: true, title: t.common.error, message: (error as any).message || JSON.stringify(error) });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleSimulateRepeat = async () => {
    if (!logEntry?.repeat_id) return;
    setIsSubmitting(true);
    try {
      const { data, error } = await supabase.rpc('simulate_single_repeat', { p_repeat_id: logEntry.repeat_id });
      if (error) throw error;
      
      if (data.status === 'ok') {
        setAlertConfig({ 
          isOpen: true, 
          title: t.common.ok, 
          message: t.repeatingExpenses.simulateSuccess 
        });
        // Reload repeat config to show updated next date and related entries
        setHasLoadedRepeat(false);
        // Refresh related entries
        const { data: related } = await supabase
          .from('log_entries')
          .select('*, expenses(*)')
          .eq('repeat_id', logEntry.repeat_id)
          .order('date', { ascending: false });
        if (related) setRelatedEntries(related);
      } else if (data.error) {
        throw new Error(data.error);
      }
    } catch (error: any) {
      console.error('Error simulating repeat:', error);
      setAlertConfig({ 
        isOpen: true, 
        title: t.common.error, 
        message: error.message || JSON.stringify(error) 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const suggestedTitle = useMemo(() => {
    const types = lineItems.map(item => {
      if (!item.type) return null;
      const typeDef = availableExpenseTypes.find((t: any) => t.type_key === item.type);
      return typeDef ? (getExpenseTypeNameFromData(typeDef, currentLanguage) || (t.expenses.typeLabels as any)[item.type] || item.type) : item.type;
    }).filter(Boolean);
    const uniqueTypes = Array.from(new Set(types)).join(', ');
    if (!uniqueTypes) return '';
    const prefix = (t.expenses as any).defaultTitlePrefix || 'Pēc noklusējuma būs -';
    return `${prefix} ${uniqueTypes}`;
  }, [lineItems, availableExpenseTypes, currentLanguage, t.expenses.typeLabels, t.expenses.defaultTitlePrefix]);

  const planType = effectivePreferences?.plan_type || 'free';
  const planConfig = (planLimitsData as any)[planType] || (planLimitsData as any)['free'];
  const countLimit = (effectivePreferences?.file_count_limit ?? 0) > 0 ? effectivePreferences!.file_count_limit : planConfig.file_count_limit;

  const [initialImages, setInitialImages] = useState<string>('[]');
  const [initialSpareParts, setInitialSpareParts] = useState<string>('[]');
  const [initialLineItemsStr, setInitialLineItemsStr] = useState<string>('[]');
  const [initialScalars, setInitialScalars] = useState<{ title: string, date: string, mileageAtExpense: string, valueAtExpense: string, isTimeEnabled: boolean, time: string } | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{ isOpen: boolean; title: string; message: string }>({ isOpen: false, title: '', message: '' });

  const imageUploadRef = useRef<ImageUploadHandle>(null);
  const sparePartFormRef = useRef<SparePartFormHandle>(null);
  // Track which image URLs were already saved when the form opened (to avoid deleting existing DB images)
  const savedImageUrlsRef = useRef<Set<string>>(new Set());
  // Helper to compare text while ignoring whitespace differences and standard HTML entities
  const normalizeText = (s: string) => {
    return (s || '')
      .replace(/&gt;/g, '>')
      .replace(/&lt;/g, '<')
      .replace(/&amp;/g, '&')
      .trim();
  };
  const hasChanges = useMemo(() => {
    // Determine what to compare against
    const compareScalars = initialScalars || {
      title: logEntry?.title || '',
      date: (logEntry?.date || new Date().toISOString()).split('T')[0],
      mileageAtExpense: (logEntry?.mileage && logEntry.mileage !== 0) ? logEntry.mileage.toString() : '',
      valueAtExpense: logEntry?.car_value?.toString() || '',
      isTimeEnabled: getInitialHasTime(logEntry),
      time: getInitialHasTime(logEntry) && logEntry?.date ? `${new Date(logEntry.date).getHours().toString().padStart(2, '0')}:${new Date(logEntry.date).getMinutes().toString().padStart(2, '0')}` : new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    };

    if (title !== compareScalars.title) return true;
    if (date !== compareScalars.date) return true;
    if (isTimeEnabled !== compareScalars.isTimeEnabled) return true;
    if (isTimeEnabled && time !== compareScalars.time) return true;
    if (mileageAtExpense !== compareScalars.mileageAtExpense) return true;
    if (valueAtExpense !== compareScalars.valueAtExpense) return true;

    if (JSON.stringify(images) !== initialImages) return true;
    if (JSON.stringify(spareParts) !== initialSpareParts) return true;

    // Detailed comparison of line items to handle entity encoding
    const initialLineItems = JSON.parse(initialLineItemsStr);
    if (lineItems.length !== initialLineItems.length) return true;
    
    for (let i = 0; i < lineItems.length; i++) {
        const item = lineItems[i];
        const initialItem = initialLineItems[i];
        if (!initialItem) return true;
        
        // Compare each field, normalizing the description
        const fields: Array<keyof ExpenseLineItemData> = [
            'id', 'type', 'title', 'amount', 'isSplitServiceCosts', 
            'amountParts', 'amountWork', 'fuelLiters', 'electricityKwh', 
            'fuelType'
        ];
        for (const field of fields) {
            if (item[field] !== initialItem[field]) return true;
        }
        
        if (normalizeText(item.description) !== normalizeText(initialItem.description)) return true;
    }
    if (locationId !== initialLocationId) return true;

    if (isRepeating !== (!!logEntry?.repeat_id)) return true;
    if (isRepeating && initialRepeatConfig) {
      if (repeatEndDate !== (initialRepeatConfig.end_date || null)) return true;
      if (repeatCopyTitle !== (initialRepeatConfig.copy_title ?? true)) return true;
      if (repeatCopyLocation !== (initialRepeatConfig.copy_location ?? true)) return true;
      if (repeatCopyDescription !== (initialRepeatConfig.copy_description ?? true)) return true;
    }

    return false;
  }, [mode, title, date, time, isTimeEnabled, mileageAtExpense, valueAtExpense, images, spareParts, lineItems, locationId, initialScalars, initialImages, initialSpareParts, initialLineItemsStr, initialLocationId, isRepeating, repeatEndDate, repeatCopyTitle, repeatCopyLocation, repeatCopyDescription, logEntry?.repeat_id, initialRepeatConfig, JSON.stringify(lineItems)]);





  const initialInitDone = useRef<string | null>(null);

  useEffect(() => {
    if (mode === 'edit' && expenseId) {
      // Only initialize if we haven't already for this specific expense
      if (initialInitDone.current === expenseId) return;

      const fetchExpenseDetails = async () => {
        let targetLogEntry: any = logEntry;

        if (!targetLogEntry || !targetLogEntry.expenses || targetLogEntry.expenses.some((e: any) => !e.description || e.description.length < 50)) {
          const { data, error } = await supabase
            .from('log_entries')
            .select('*, expenses(*), log_entry_images(id), log_entry_parts(spare_part_id), location:locations(*)')
            .eq('id', expenseId)
            .single();
          if (!error && data) {
            targetLogEntry = {
              ...data,
              imageCount: data.log_entry_images?.length || 0,
            };
          }
        }

        if (!targetLogEntry) return;

        const tDate = getDateInTimezone(targetLogEntry.date || new Date().toISOString(), preferences?.timezone);
        const tMileage = (targetLogEntry.mileage && targetLogEntry.mileage !== 0) ? targetLogEntry.mileage.toString() : '';
        const tTitle = targetLogEntry.title || '';
        const tValue = targetLogEntry.car_value?.toString() || '';

        const hasTime = getInitialHasTime(targetLogEntry);
        let parsedTime = getTimeInTimezone(new Date().toISOString(), preferences?.timezone);
        if (hasTime && targetLogEntry.date) {
            parsedTime = getTimeInTimezone(targetLogEntry.date, preferences?.timezone);
        }

        setTitle(tTitle);
        setMileageAtExpense(tMileage);
        setDate(tDate);
        setValueAtExpense(tValue);
        setIsTimeEnabled(hasTime);
        setTime(parsedTime);

        setInitialScalars({ title: tTitle, date: tDate, mileageAtExpense: tMileage, valueAtExpense: tValue, isTimeEnabled: hasTime, time: parsedTime });

        setLocationId(targetLogEntry.location_id || null);
        setInitialLocationId(targetLogEntry.location_id || null);
        if (targetLogEntry.location && Object.keys(targetLogEntry.location).length > 0) {
          setSelectedLocationObj(targetLogEntry.location);
        } else if (targetLogEntry.location_id) {
          const locRes = await supabase.from('locations').select('*').eq('id', targetLogEntry.location_id).single();
          if (locRes.data) setSelectedLocationObj(locRes.data);
        }

        if (targetLogEntry.expenses && targetLogEntry.expenses.length > 0) {

          // valueAtExpense has already been set from log_entries above
          const newLineItems = targetLogEntry.expenses.map((exp: any) => ({
            id: exp.id,
            type: exp.type || '',
            title: exp.title || '',
            amount: exp.amount?.toString() || '',
            isSplitServiceCosts: (exp.amount_parts || 0) > 0 || (exp.amount_work || 0) > 0,
            amountParts: exp.amount_parts?.toString() || '',
            amountWork: exp.amount_work?.toString() || '',
            fuelLiters: exp.fuel_liters?.toString() || '',
            electricityKwh: exp.electricity_kwh?.toString() || '',
            fuelType: exp.fuel_type || '',
            description: exp.description || '',
            is_initial_purchase: exp.is_initial_purchase || false
          }));
          setLineItems(newLineItems);
          setInitialLineItemsStr(JSON.stringify(newLineItems));
        } else {
          setInitialLineItemsStr('[]');
        }

        loadSpareParts(expenseId);
        loadExpenseImages(expenseId);
        initialInitDone.current = expenseId;
        setIsFormInitialized(true);
      };

      fetchExpenseDetails();
    } else {
      const initItem = { id: generateUUID(), type: '', title: '', amount: '', isSplitServiceCosts: false, amountParts: '', amountWork: '', fuelLiters: '', electricityKwh: '', fuelType: '', description: '' };
      setLineItems([initItem]);
      setInitialLineItemsStr(JSON.stringify([initItem]));
      setImages([]);
      setInitialImages('[]');
      setSpareParts([]);
      setInitialSpareParts('[]');
      setLocationId(null);
      setInitialLocationId(null);
      setSelectedLocationObj(null);
      initialInitDone.current = null;
      setIsFormInitialized(true);
      
      // Initialize initialScalars for comparison in add mode
      const isTimeOnList = getInitialHasTime(logEntry);
      setInitialScalars({
        title: logEntry?.title || '',
        date: getDateInTimezone(logEntry?.date || new Date().toISOString(), preferences?.timezone),
        mileageAtExpense: (logEntry?.mileage && logEntry.mileage !== 0) ? logEntry.mileage.toString() : '',
        valueAtExpense: logEntry?.car_value?.toString() || '',
        isTimeEnabled: isTimeOnList,
        time: isTimeOnList && logEntry?.date ? getTimeInTimezone(logEntry.date, preferences?.timezone) : getTimeInTimezone(new Date().toISOString(), preferences?.timezone)
      });
    }
  }, [expenseId, mode, logEntry]);




  // Pre-fill from AI extracted data (clipboard flow)
  useEffect(() => {
    const state = (location as any).state;
    if (state?.prefilledData && mode === 'add') {
      const data = state.prefilledData;
      if (data.title) setTitle(data.title);
      if (data.date) setDate(data.date);
      if (data.temp_image_url) {
        setImages([{ url: data.temp_image_url }]);
      }
      setLineItems([{
        id: generateUUID(),
        type: data.type || '',
        title: data.title || '',
        amount: data.amount ? data.amount.toString() : '',
        isSplitServiceCosts: false,
        amountParts: '',
        amountWork: '',
        fuelLiters: data.fuel_liters ? data.fuel_liters.toString() : '',
        electricityKwh: '',
        fuelType: '',
        description: data.description || ''
      }]);
    }
  }, [(location as any).state, mode]);


  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // iOS Keyboard Fix: Use Visual Viewport API
  useEffect(() => {
    if (!window.visualViewport) return;

    const vvp = window.visualViewport;
    const handleViewportChange = () => {
      if (containerRef.current) {
        containerRef.current.style.height = `${vvp.height}px`;
        containerRef.current.style.top = `${vvp.offsetTop}px`;

        if (window.scrollY !== 0) {
          window.scrollTo(0, 0);
        }
      }
    };

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
        const isMobile = window.innerWidth < 768;
        if (isMobile) {
          setTimeout(() => {
            target.scrollIntoView({ block: 'center', behavior: 'smooth' });
            window.scrollTo(0, 0);
          }, 300);
        }
      }
    };

    vvp.addEventListener('resize', handleViewportChange);
    vvp.addEventListener('scroll', handleViewportChange);
    window.addEventListener('focusin', handleFocusIn);

    handleViewportChange();

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100dvh';

    return () => {
      vvp.removeEventListener('resize', handleViewportChange);
      vvp.removeEventListener('scroll', handleViewportChange);
      window.removeEventListener('focusin', handleFocusIn);
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, []);

  const loadExpenseImages = async (expenseId: string) => {
    const { data, error } = await supabase
      .from('log_entry_images')
      .select('image_url, image_url_thumb, comment, file_size_bytes, is_receipt')
      .eq('log_entry_id', expenseId)
      .order('display_order');

    if (!error && data) {
      const imgData = data.map(img => ({
        url: img.image_url,
        thumbnail: img.image_url_thumb,
        comment: img.comment,
        sizeBytes: img.file_size_bytes,
        is_receipt: !!img.is_receipt
      }));
      setImages(imgData);
      setInitialImages(JSON.stringify(imgData));
      // Mark these as "already saved" so cancel won't try to delete them from temp_files
      savedImageUrlsRef.current = new Set(imgData.map(img => img.url));
    }
  };

  const loadSpareParts = async (expenseId: string) => {
    const { data: links, error } = await supabase
      .from('log_entry_parts')
      .select(`
        quantity,
        price_per_unit,
        comment,
        purchased_at,
        spare_parts (
          id, name, manufacturer, part_code, link, comment, created_at,
          total_uses, avg_price, last_used_at,
          spare_part_images ( * )
        )
      `)
      .eq('log_entry_id', expenseId);

    if (error) {
      console.error('Error loading spare parts:', error);
      return;
    }

    if (links && links.length > 0) {
      const parts: SparePartFormData[] = links.map((link: any) => {
        const part = link.spare_parts;
        const images = part.spare_part_images?.sort((a: any, b: any) => (a.display_order || 0) - (b.display_order || 0)).map((img: any) => ({
          url: img.image_url,
          thumbnail: img.image_url_thumb,
          comment: img.comment || '',
          sizeBytes: img.file_size_bytes,
          is_receipt: !!img.is_receipt,
          log_entry_id: img.log_entry_id,
          note_id: img.note_id
        })) || [];

        return {
          // Master record fields
          id: part.id,
          name: part.name,
          manufacturer: part.manufacturer || '',
          part_code: part.part_code || undefined,
          link: part.link || undefined,
          master_comment: part.comment || undefined,
          images: images,
          // Transaction fields (from junction table)
          quantity: link.quantity || 1,
          price_per_unit: link.price_per_unit || undefined,
          comment: link.comment || undefined,
          // Statistics
          total_uses: part.total_uses,
          avg_price: part.avg_price,
          last_used_at: part.last_used_at
        };
      });

      setSpareParts(parts);
      setInitialSpareParts(JSON.stringify(parts));
    } else {
      setInitialSpareParts('[]');
    }
  };


  const handleDeleteCall = async () => {
    if (isReadOnly || !logEntry || mode !== 'edit') return;
    setIsSubmitting(true);
    try {
      // 1-3. Delete dependent records (images, parts, expenses)
      // We use allSettled and ignore errors here because some records or relations 
      // might already be missing (like the 'expense_spare_parts' error you encountered).
      // This ensures we at least try to delete the main log entry.
      await Promise.allSettled([
        supabase.from('log_entry_images').delete().eq('log_entry_id', logEntry.id),
        supabase.from('log_entry_parts').delete().eq('log_entry_id', logEntry.id),
        supabase.from('expenses').delete().eq('log_entry_id', logEntry.id)
      ]);

      // 4. Finally delete the log entry itself
      const { error, status } = await supabase.from('log_entries').delete().eq('id', logEntry.id);

      if (error && status !== 404) {
        // If it's the "relation does not exist" error, it's likely a stale trigger.
        // We show the error but allow the user to know what's happening.
        if ((error as any).code === '42P01') {
           throw new Error(`Database trigger error: ${error.message}. Please apply the pending database migration to fix stale references.`);
        }
        throw error;
      }

      queryClient.invalidateQueries({ queryKey: CAR_KEYS.all });
      queryClient.invalidateQueries({ queryKey: LOG_ENTRY_KEYS.all });
      queryClient.invalidateQueries({ queryKey: GALLERY_KEYS.all });
      
      dataManager.invalidateLogEntries(carId);
      dataManager.invalidateGallery(carId);
      onClose();
      onExpenseAdded(logEntry); // Trigger refresh in parent
    } catch (error) {
      console.error('Error deleting record:', error);
      setAlertConfig({ isOpen: true, title: t.common.error, message: (error as any).message || JSON.stringify(error) });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = () => setShowDeleteModal(true);

  const calculateNextRepeatDate = (baseDate: string, endDate?: string | null) => {
    if (!baseDate || typeof baseDate !== 'string') return '';
    try {
      const pureDate = baseDate.split('T')[0];
      const parts = pureDate.split(/[-/.]/);
      if (parts.length !== 3) return '';
      const [y, m, d] = parts.map(Number);
      if (isNaN(y) || isNaN(m) || isNaN(d)) return '';
      
      const targetDay = d;
      let currentMonth = m;
      let currentYear = y;
      
      const now = new Date();
      now.setHours(23, 59, 59, 999);
      
      const endLimit = endDate ? new Date(endDate) : null;
      if (endLimit) endLimit.setHours(23, 59, 59, 999);

      let nextDateObj: Date;
      let iterations = 0;

      while (true) {
        iterations++;
        // Increment month
        currentMonth++;
        if (currentMonth > 12) {
          currentMonth = 1;
          currentYear++;
        }
        
        // Find last day of THAT month to handle 31 -> 30 logic
        // new Date(y, m, 0) returns last day of previous month m
        const lastDayOfThatMonth = new Date(currentYear, currentMonth, 0).getDate();
        const actualDay = Math.min(targetDay, lastDayOfThatMonth);
        
        nextDateObj = new Date(currentYear, currentMonth - 1, actualDay, 12, 0, 0);

        if (nextDateObj > now) {
          break;
        }

        // Safety break
        if (iterations > 240) return null;
      }

      if (endLimit && nextDateObj > endLimit) return null;

      const nextY = nextDateObj.getFullYear();
      const nextM = nextDateObj.getMonth() + 1;
      const nextD = nextDateObj.getDate();
      return `${nextY}-${String(nextM).padStart(2, '0')}-${String(nextD).padStart(2, '0')}`;
    } catch (e) {
      return '';
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (isReadOnly || isSubmitting) return;

    // Validate Expense Types
    const missingType = lineItems.some(item => !item.type || item.type.trim() === '');
    if (missingType) {
      setAlertConfig({ isOpen: true, title: t.common.error, message: (t.common as any).mandatoryFieldMissing || 'Please select an Expense Type.*' });
      return;
    }

    setIsSubmitting(true);

    try {
      const isInitialPurchase = lineItems.some((li: any) => li.is_initial_purchase);

      let finalDateStr = convertToUTC(date, '12:00', preferences?.timezone);
      if (isTimeEnabled && time) {
        finalDateStr = convertToUTC(date, time, preferences?.timezone);
      }

      const logEntryData: any = {
        car_id: carId,
        user_id: effectiveUserId,
        date: finalDateStr,
        mileage: mileageAtExpense ? parseInt(mileageAtExpense) : null,
        car_value: valueAtExpense ? parseFloat(valueAtExpense) : null,
        time_used: isTimeEnabled,
        title: isInitialPurchase ? '' : (title?.trim() || null),
        location_id: locationId,
        is_initial_purchase_entry: isInitialPurchase,
        is_temporary: false
      };

      let logEntryResult;
      if (mode === 'edit' && logEntry) {
        logEntryResult = await supabase.from('log_entries').update(logEntryData).eq('id', logEntry.id).select().maybeSingle();
      } else {
        logEntryResult = await supabase.from('log_entries').insert([{ ...logEntryData, id: expenseId }]).select().maybeSingle();
      }

      if (logEntryResult.error) throw logEntryResult.error;
      const finalLogEntry = logEntryResult.data;

      // Handle array of expenses!
      const expensesToInsert = lineItems.map(item => {
        const expenseData: any = {
          car_id: carId,
          user_id: effectiveUserId,
          log_entry_id: finalLogEntry.id,
          type: item.type,
          title: item.title,
          amount: item.amount ? parseFloat(item.amount) : null,
          amount_parts: (isServiceType(item.type) && item.isSplitServiceCosts && item.amountParts) ? parseFloat(item.amountParts) : 0,
          amount_work: (isServiceType(item.type) && item.isSplitServiceCosts && item.amountWork) ? parseFloat(item.amountWork) : 0,
          description: isInitialPurchase ? (item.description || t.common.purchasedCarTitle) : (item.description || '').trim(),
          date: finalDateStr,
          image_url: images.length > 0 ? images[0].url : '',
          image_url_thumb: images.length > 0 ? (images[0] as any).thumbnail || images[0].url : '',
          ai_extracted: {},
          fuel_type: (item.type === 'fuel' || item.type === 'electricity') ? (item.fuelType || null) : null,
          fuel_liters: (item.type === 'fuel' && item.fuelLiters) ? parseFloat(item.fuelLiters) : null,
          electricity_kwh: (item.type === 'electricity' && item.electricityKwh) ? parseFloat(item.electricityKwh) : null
        };

        if (mode === 'edit' && logEntry && logEntry.expenses && logEntry.expenses.some(e => e.id === item.id)) {
          expenseData.id = item.id;
        } else {
          expenseData.id = item.id; // Just reuse the generated one
        }
        return expenseData;
      });

      const backgroundPromises: Promise<any>[] = [];

      backgroundPromises.push((async () => {
        const { data: upsertedExpenses, error: expensesError } = await supabase.from('expenses').upsert(expensesToInsert).select();
        if (expensesError) throw expensesError;

        if (mode === 'edit' && logEntry?.expenses) {
          const currentIds = lineItems.map(li => li.id);
          const removedIds = logEntry.expenses.filter(e => !currentIds.includes(e.id)).map(e => e.id);
          if (removedIds.length > 0) {
            await supabase.from('expenses').delete().in('id', removedIds);
          }
        }

        const firstInsertedExpense = upsertedExpenses?.[0];

        // Sync with Car if this is the purchase record
        const isPurchaseRecord = firstExpense?.is_initial_purchase || 
                              (selectedCar as any)?.purchase_expense_id === firstExpense?.id ||
                              (selectedCar as any)?.purchase_log_entry_id === finalLogEntry.id; // Legacy fallback

        if (isPurchaseRecord) {
          const carUpdate: any = {};
          if (date) carUpdate.bought_at = convertToUTC(date, '12:00', preferences?.timezone);
          if (firstInsertedExpense?.amount !== undefined && firstInsertedExpense?.amount !== null) {
            carUpdate.purchase_price = firstInsertedExpense.amount;
          }
          if (mileageAtExpense) {
            carUpdate.purchase_mileage = parseInt(mileageAtExpense);
          }
          carUpdate.purchase_expense_id = firstInsertedExpense?.id;

          if (Object.keys(carUpdate).length > 0) {
            await supabase.from('cars').update(carUpdate).eq('id', carId);
          }
          dataManager.invalidateCars(effectiveUserId);
          // Query Client invalidation moved here to ensure it fires along with the car save
          queryClient.invalidateQueries({ queryKey: CAR_KEYS.slug(selectedCar.slug) });
        }
      })());

      // Handle Repeating Expense
      backgroundPromises.push((async () => {
        if (isRepeating) {
          const repeatData: any = {
            car_id: carId,
            user_id: effectiveUserId,
            interval_type: repeatIntervalType,
            interval_value: repeatIntervalValue,
            start_date: date,
            next_generation_date: calculateNextRepeatDate(date, repeatEndDate),
            repeat_day: new Date(date).getDate(),
            end_date: repeatEndDate,
            copy_title: repeatCopyTitle,
            copy_location: repeatCopyLocation,
            copy_description: repeatCopyDescription,
            template_log_entry_id: finalLogEntry.id
          };

          const { data: repeatResult, error: repeatError } = await supabase
            .from('log_entry_repeats')
            .upsert([mode === 'edit' && logEntry?.repeat_id ? { ...repeatData, id: logEntry.repeat_id } : repeatData])
            .select()
            .single();

          if (!repeatError && repeatResult && finalLogEntry.repeat_id !== repeatResult.id) {
            await supabase.from('log_entries').update({ repeat_id: repeatResult.id }).eq('id', finalLogEntry.id);
            finalLogEntry.repeat_id = repeatResult.id;
          }
        } else if (mode === 'edit' && logEntry?.repeat_id) {
          // Clear the link on the current entry so the icon disappears
          await supabase.from('log_entries').update({ repeat_id: null }).eq('id', finalLogEntry.id);
          // Also stop future generations for this chain by setting next_generation_date to null
          await supabase.from('log_entry_repeats').update({ next_generation_date: null }).eq('id', logEntry.repeat_id);
          // Ensure the locally returned object matches the DB state for the parent update
          finalLogEntry.repeat_id = null;
        }
      })());

      // ──────────────────────────────────────────────────────────────────────────
      // AUTO-SYNC CAR DATA TO LINKED INFO ENTRY
      // ──────────────────────────────────────────────────────────────────────────
      /* 
      // FEATURE DISABLED: We no longer create background 'info' records for mileage/value
      // This is now handled by displaying the data directly on the main log entry
      if (finalLogEntry) {
        const isInfoEntry = !!(logEntry?.linked_origin_id || finalLogEntry.linked_origin_id);
        const hasData = !!(mileageAtExpense || valueAtExpense);
        
        // Final values to sync (directly from the state to be absolutely sure)
        const currentMileage = mileageAtExpense ? parseInt(mileageAtExpense) : null;
        const currentCarValue = valueAtExpense ? parseFloat(valueAtExpense) : null;

        if (isInfoEntry) {
          // Current record is an Info record -> Sync changes back to the origin record
          const originId = logEntry?.linked_origin_id || finalLogEntry.linked_origin_id;
          if (originId) {
            // Update the main log entry record (origin)
            const { error: originLogErr } = await supabase.from('log_entries').update({
              date,
              mileage: currentMileage,
              car_value: currentCarValue,
              location_id: locationId
            }).eq('id', originId);
            if (originLogErr) throw originLogErr;

            // Update all associated expenses for the origin record
            const { error: originExpErr } = await supabase.from('expenses').update({
              date
            }).eq('log_entry_id', originId);
            if (originExpErr) throw originExpErr;
          }
        } else {
          // Current record is an Origin record -> Update or Create an Info record
          if (hasData) {
            let currentInfoId = logEntry?.linked_info_id || finalLogEntry.linked_info_id;

            // Verify if Info record still exists from Supabase
            if (currentInfoId) {
              const { data: check } = await supabase.from('log_entries').select('id').eq('id', currentInfoId).maybeSingle();
              if (!check) currentInfoId = null;
            }

            const infoLogData = {
              car_id: carId,
              user_id: effectiveUserId,
              date,
              mileage: currentMileage,
              car_value: currentCarValue,
              location_id: locationId,
              linked_origin_id: finalLogEntry.id,
              title: null // Automated info records have no title so UI can show labels
            };

            if (currentInfoId) {
              // 1. Update existing Info record
              const { error: updateLogErr } = await supabase.from('log_entries').update(infoLogData).eq('id', currentInfoId);
              if (updateLogErr) throw updateLogErr;
              
              // 2. Update the associated 'info' expense(s)
              const { error: updateExpErr } = await supabase.from('expenses').update({
                date,
                title: '', // DB constraint requires non-null title
                description: '',
                type: 'info', // Strict 'info' type
                amount: 0
              }).eq('log_entry_id', currentInfoId);
              if (updateExpErr) throw updateExpErr;
            } else {
              // Create NEW Info entry
              const newInfoId = generateUUID();
              const { data: newInfo, error: insertLogErr } = await supabase.from('log_entries').insert([{ ...infoLogData, id: newInfoId }]).select().maybeSingle();
              if (insertLogErr) throw insertLogErr;
              
              if (newInfo) {
                // Link origin record to info record
                const { error: linkErr } = await supabase.from('log_entries').update({ linked_info_id: newInfoId }).eq('id', finalLogEntry.id);
                if (linkErr) throw linkErr;
                
                // Also update local object reference
                finalLogEntry.linked_info_id = newInfoId;

                // Create the required Info expense line
                const { error: insertExpErr } = await supabase.from('expenses').insert([{
                  id: generateUUID(),
                  car_id: carId,
                  user_id: effectiveUserId,
                  log_entry_id: newInfoId,
                  type: 'info', // MUST be info!
                  title: '', // Empty string to satisfy NOT NULL constraint
                  amount: 0,
                  date,
                  description: ''
                }]);
                if (insertExpErr) throw insertExpErr;
              }
            }
          } else if (logEntry?.linked_info_id || finalLogEntry.linked_info_id) {
            // Data cleared -> cleanup Info entry
            const infoId = logEntry?.linked_info_id || finalLogEntry.linked_info_id;
            await supabase.from('log_entries').delete().eq('id', infoId);
            await supabase.from('log_entries').update({ linked_info_id: null }).eq('id', finalLogEntry.id);
            finalLogEntry.linked_info_id = null;
          }
        }
      }
      */

      if (finalLogEntry) {
        // Parallelize Image, Parts, Mileage, and Task cleanup queries
        backgroundPromises.push((async () => {
          await Promise.allSettled([
            supabase.from('log_entry_images').delete().eq('log_entry_id', finalLogEntry.id),
            supabase.from('log_entry_parts').delete().eq('log_entry_id', finalLogEntry.id)
          ]);
  
          if (images.length > 0) {
            const imageRecords = images.map((img, index) => ({
              log_entry_id: finalLogEntry.id,
              image_url: img.url,
              image_url_thumb: img.thumbnail || img.url,
              comment: img.comment || '',
              display_order: index,
              user_id: effectiveUserId,
              file_size_bytes: img.sizeBytes || 0,
              is_receipt: img.is_receipt || false
            }));
            const { error: imgError } = await supabase.from('log_entry_images').insert(imageRecords);
            if (imgError) console.warn('Failed to insert images:', imgError);
          }
        })());

        backgroundPromises.push((async () => {
          if (mileageAtExpense && parseInt(mileageAtExpense) > 0) {
            const { data: carData } = await supabase.from('cars').select('mileage').eq('id', carId).single();
            if (carData) {
              if (carData.mileage === null || parseInt(mileageAtExpense) > carData.mileage) {
                await supabase.from('cars').update({ mileage: parseInt(mileageAtExpense) }).eq('id', carId);
                dataManager.invalidateCars(effectiveUserId);
              }
            }
          }
        })());

        backgroundPromises.push((async () => {
          const validSpareParts = spareParts.filter(part => part.name.trim() !== '');
          for (const part of validSpareParts) {
            const { data: sparePartData, error: partError } = await supabase.from('spare_parts').upsert({
              id: part.id, car_id: carId, name: part.name, manufacturer: part.manufacturer || null, part_code: part.part_code || null, link: part.link || null, user_id: effectiveUserId, is_temporary: false
            }).select().single();
  
            if (partError) {
              console.error('Error upserting spare part:', partError);
              continue;
            }
  
            if (sparePartData) {
              const { error: linkError } = await supabase.from('log_entry_parts').insert({
                log_entry_id: finalLogEntry.id, spare_part_id: sparePartData.id, quantity: part.quantity || 1, price_per_unit: part.price_per_unit || null, comment: part.comment || null, purchased_at: new Date().toISOString()
              });
              if (linkError) console.warn('Failed to insert log entry part link:', linkError);
  
              if (part.images) {
                await supabase.from('spare_part_images').delete().eq('spare_part_id', sparePartData.id);
                if (part.images.length > 0) {
                  const imageRecords = part.images.map((img: any, index: number) => ({
                    spare_part_id: sparePartData.id, 
                    image_url: img.url, 
                    image_url_thumb: img.thumbnail || img.url, 
                    comment: img.comment || null, 
                    display_order: index, 
                    user_id: effectiveUserId, 
                    file_size_bytes: img.sizeBytes || 0,
                    is_receipt: img.is_receipt || false,
                    log_entry_id: img.log_entry_id || (img.note_id ? null : (expenseId || finalLogEntry.id)),
                    note_id: img.note_id || null
                  }));
                  await supabase.from('spare_part_images').insert(imageRecords);
                }
              }
            }
          }
        })());

        backgroundPromises.push((async () => {
          if (completedTaskIds.length > 0) {
            const { error: taskError } = await supabase
              .from('car_tasks')
              .update({ 
                last_performed_date: finalDateStr,
                last_performed_mileage: mileageAtExpense ? parseInt(mileageAtExpense) : null
              })
              .in('id', completedTaskIds);
            if (taskError) {
              console.warn('Failed to update completed tasks:', taskError);
            }
          }
        })());

        backgroundPromises.push((async () => {
          // Update database counters so list redraws correctly
          const finalImageCount = images.reduce((sum, item) => sum + (Array.isArray(item.url) ? item.url.length : 1), 0);
          const finalPartCount = spareParts.length;
          await supabase.from('log_entries').update({
              image_count: finalImageCount,
              part_count: finalPartCount
          }).eq('id', finalLogEntry.id);
        })());

        // Update linked location status if it was temporary
        if (locationId) {
          backgroundPromises.push(
            supabase.from('locations').update({ is_temporary: false }).eq('id', locationId)
          );
        }

        // WE AWAIT EVERYTHING CONCURRENTLY TO SLASH DB WAIT TIME
        await Promise.allSettled(backgroundPromises);
      }

      const prefilledData = (location as any).state?.prefilledData;
      if (prefilledData?.temp_image_path) {
        supabase.storage.from('expense-images').remove([prefilledData.temp_image_path]).catch(e => {});
      }

      // 1. Instantly Close the UI
      onClose();
      
      // 2. Dispatch the Cache update (now deeply optimized inside handleExpenseAdded)
      onExpenseAdded(finalLogEntry);
      
      if (mode === 'add') {
        trackUserActivity('created_expense', carId);
      }
      
      // 3. Final sync of all reminders for this car after possible mileage or task updates
      if (finalLogEntry) {
         syncAllCarTaskReminders(carId).catch(err => console.warn('Background reminder sync failed', err));
      }

    } catch (error: any) {
      console.error('Error saving expense:', error);
      let errorMessage = error.message || JSON.stringify(error);
      if (error.code === '23514') {
        errorMessage = t.common.mandatoryFieldsError;
      }
      setAlertConfig({ isOpen: true, title: t.common.error, message: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (showSaveAction && hasChanges) setShowConfirmModal(true);
    else {
      // Delete any temp files that were uploaded during this session but not saved
      const unsavedUrls = images
        .map(img => img.url)
        .filter(url => url && !savedImageUrlsRef.current.has(url));
      if (unsavedUrls.length > 0) {
        deleteTempFiles(unsavedUrls);
      }
      onClose();
    }
  };

  const showSaveAction = !isReadOnly && isFormInitialized && (mode === 'add' || hasChanges || logEntry?.is_temporary);

  // Common fields as variables for cleaner JSX
  let dateTitleStr = t.expenses.date;
  if (isTimeEnabled) {
      dateTitleStr += ` & ${(t.expenses as any).timeLabel || "Time"}`;
  }

  const dateField = (
    <div className="space-y-4">
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <DateInput
            label={dateTitleStr}
            value={date}
            onChange={setDate}
            disabled={isReadOnly}
            language={language}
          />
        </div>
        {!isTimeEnabled && !isReadOnly && (
          <button
            type="button"
            onClick={() => {
              setTime(getTimeInTimezone(new Date().toISOString(), preferences?.timezone));
              setIsTimeEnabled(true);
            }}
            className="p-3 bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-all mb-0.5"
            title={(t.expenses as any).addTimeLabel || "Add time"}
          >
            <Clock size={22} />
          </button>
        )}
      </div>
      
      {isTimeEnabled && (
        <div className="flex items-center gap-3">
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            disabled={isReadOnly}
            className="flex-1 px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-primary disabled:bg-gray-50 dark:disabled:bg-slate-900/20 disabled:text-gray-500"
          />
          {!isReadOnly && (
            <button
              type="button"
              onClick={() => setIsTimeEnabled(false)}
              className="p-3 bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 rounded-xl hover:bg-red-100 dark:hover:bg-red-500/20 transition-all font-bold"
              title={(t.expenses as any).removeTimeLabel || "Remove time"}
            >
              <Clock size={22} />
            </button>
          )}
        </div>
      )}
    </div>
  );

  const titleField = (
    <div>
      <FormLabel>
        {(t.expenses as any).logEntryTitleLabel || "Ieraksta nosaukums (Neobligāts)"}
      </FormLabel>
      <input
        type="text"
        disabled={isReadOnly}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-primary disabled:bg-gray-50 dark:disabled:bg-slate-900/20 disabled:text-gray-500"
        placeholder={suggestedTitle || (t.expenses as any).autoTitlePlaceholder || "Aizpildīsies automātiski..."}
      />
    </div>
  );

  const addAnotherButton = !isReadOnly && (
    <div className="space-y-3">
      <button
        type="button"
        onClick={handleAddLineItem}
        className="w-full py-4 border-2 border-dashed border-gray-200 dark:border-white/10 rounded-2xl flex items-center justify-center gap-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-white/20 hover:bg-gray-50 dark:hover:bg-white/5 transition-all"
      >
        <Plus size={20} />
        <span className="font-semibold">{(t.expenses as any).addAnother || 'Pievienot vēl cita veida izmaksu'}</span>
      </button>
      <p className="text-xs text-gray-500 dark:text-gray-400 text-center px-4 leading-relaxed">
        {(t.expenses as any).addAnotherHint || 'Ja šī ieraksta ietvaros bija vairāku veidu izmaksas, varat pievienot arī tos, lai auto statistika būtu pareiza.'}
      </p>
    </div>
  );

  return (
    <div className="fixed inset-0 flex flex-col h-full overflow-hidden z-50 bg-gradient-theme" ref={containerRef}>
      <NavigationBar
        title={mode === 'edit' ? (isReadOnly ? t.expenses.details : t.expenses.editExpense) : t.expenses.newExpense}
        onBack={handleBack}
        fixed={false}
        rightAction={showSaveAction ? {
          icon: isSubmitting ? Loader2 : Save,
          onClick: () => handleSubmit(),
          disabled: isSubmitting,
          className: isSubmitting ? 'animate-spin' : ''
        } : undefined}
      />



      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <form id="expense-form" onSubmit={handleSubmit} className="p-4 space-y-5 pb-10 max-w-2xl mx-auto">

          {lineItems.length === 1 ? (
            <>
              {dateField}
              <ExpenseLineItem
                item={lineItems[0]}
                onChange={handleLineItemChange}
                isReadOnly={isReadOnly}
                availableExpenseTypes={availableExpenseTypes}
                selectedCar={selectedCar}
                effectivePreferences={effectivePreferences}
                currentLanguage={currentLanguage}
                variant="flat"
              >
                {titleField}
              </ExpenseLineItem>
              {addAnotherButton}
            </>
          ) : (
            <>
              {dateField}
              {titleField}
              <div className="pt-2 pb-2">
                <div className="h-px w-full bg-gray-200 dark:bg-white/10" />
              </div>
              <div className="space-y-4">
                {lineItems.map((item) => (
                  <ExpenseLineItem
                    key={item.id}
                    item={item}
                    onChange={handleLineItemChange}
                    onRemove={lineItems.length > 1 ? handleRemoveLineItem : undefined}
                    isReadOnly={isReadOnly}
                    availableExpenseTypes={availableExpenseTypes}
                    selectedCar={selectedCar}
                    effectivePreferences={effectivePreferences}
                    currentLanguage={currentLanguage}
                  />
                ))}
                {addAnotherButton}
              </div>
            </>
          )}

          <div className="pt-2 pb-2">
            <div className="h-px w-full bg-gray-200 dark:bg-white/10" />
          </div>

          {(spareParts.length > 0 || !isReadOnly) && (
            <div className="pt-2">
              <div className="flex items-center justify-between mb-2 px-1">
                <span className={theme.typography.sectionTitle}>
                  {t.spareParts.title}{spareParts.length > 0 ? ` (${spareParts.length})` : ''}
                </span>
                {(!isReadOnly) && (
                  <button
                    type="button"
                    onClick={() => sparePartFormRef.current?.openSelector()}
                    disabled={isReadOnly}
                    className={`bg-blue-600 dark:bg-blue-500 rounded-full p-2 text-white hover:bg-blue-700 dark:hover:bg-blue-400 transition-colors shadow-sm ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Plus size={22} strokeWidth={2.5} />
                  </button>
                )}
              </div>
              <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm p-4">
                {spareParts.length === 0 && !isReadOnly && (
                  <p className="text-sm text-gray-400 dark:text-gray-600 italic mb-3">
                    {t.spareParts.noPartsYet || 'Nav neviena rezerves detaļa pievienota'}
                  </p>
                )}
                <SparePartFormV2
                  ref={sparePartFormRef}
                  carId={carId}
                  spareParts={spareParts}
                  onChange={setSpareParts}
                  readOnly={isReadOnly}
                  preferences={effectivePreferences}
                  slug={selectedCar?.slug || ''}
                  isAdminView={outletContext.isAdminView}
                  hideAddButton={true}
                  currentExpenseId={expenseId}
                />
              </div>
            </div>
          )}

          {lineItems.some(item => isServiceType(item.type)) && mode === 'add' && tasks && tasks.length > 0 && (
            <div className={`p-5 rounded-[2rem] border ${theme.colors.cardBorder} ${theme.colors.cardBg} transition-all`}>
              <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                      <CheckCircle2 size={24} className="text-slate-600 dark:text-slate-400" />
                  </div>
                  <div>
                      <h3 className="font-bold text-lg text-slate-900 dark:text-white">Completed Tasks</h3>
                      <p className="text-sm font-medium text-slate-500">Mark scheduled tasks as completed</p>
                  </div>
              </div>
              
              <div className="space-y-3">
                  {tasks.map((task) => (
                      <label key={task.id} className="flex items-start gap-4 p-3 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                          <input 
                              type="checkbox" 
                              className="mt-1 w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                              checked={completedTaskIds.includes(task.id)}
                              onChange={(e) => {
                                  if (e.target.checked) setCompletedTaskIds(prev => [...prev, task.id]);
                                  else setCompletedTaskIds(prev => prev.filter(id => id !== task.id));
                              }}
                          />
                          <div>
                              <p className="font-bold text-slate-900 dark:text-white">{task.name}</p>
                              <p className="text-xs font-bold text-slate-500 uppercase">{task.action_type || 'replace'}</p>
                          </div>
                      </label>
                  ))}
              </div>
            </div>
          )}

          {(images.length > 0 || !isReadOnly) && (
            <div>
              <div className="flex items-center justify-between mb-2 px-1">
                <span className={theme.typography.sectionTitle}>
                  {t.expenses.images}{images.length > 0 ? ` (${images.length})` : ''}
                </span>
                {!isReadOnly && (
                  <button
                    type="button"
                    onClick={() => imageUploadRef.current?.openFileDialog()}
                    className="bg-blue-600 dark:bg-blue-500 rounded-full p-2 text-white hover:bg-blue-700 dark:hover:bg-blue-400 transition-colors shadow-sm"
                  >
                    <Plus size={22} strokeWidth={2.5} />
                  </button>
                )}
              </div>
              <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm p-4">
                {images.length === 0 && !isReadOnly && (
                  <p className="text-sm text-gray-400 dark:text-gray-600 italic mb-3">{t.dashboard.noFiles}</p>
                )}
                <ImageUpload
                  ref={imageUploadRef}
                  images={images}
                  onChange={setImages}
                  folder="expenses"
                  maxImages={countLimit || 10}
                  showComments={true}
                  readOnly={isReadOnly}
                  type="expense"
                  itemId={expenseId}
                  hideUploadButton={true}
                  carId={carId}
                  showReceiptToggle={true}
                />
              </div>
            </div>
          )}

          {(() => {
            const canShowLocation = !isReadOnly || selectedCar?.share_locations || isAdminView;
            if (!canShowLocation || (!selectedLocationObj && isReadOnly)) return null;

            return (
              <div className="pt-2">
                <div className="flex items-center justify-between mb-2 px-1">
                  <span className={theme.typography.sectionTitle}>
                    {(t.locations as any)?.singleTitle || (t.locations as any)?.title || 'Location'}
                  </span>
                  {(!isReadOnly && !selectedLocationObj) && (
                    <button
                      type="button"
                      onClick={() => setIsLocationModalOpen(true)}
                      disabled={isReadOnly}
                      className={`bg-blue-600 dark:bg-blue-500 rounded-full p-2 text-white hover:bg-blue-700 dark:hover:bg-blue-400 transition-colors shadow-sm ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Plus size={22} strokeWidth={2.5} />
                    </button>
                  )}
                </div>

                {enrichedLocation ? (
                  <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm relative">
                    <LocationItem
                      location={enrichedLocation}
                      compact
                      onClick={isReadOnly ? () => {
                        const prefix = isAdminView ? '/admin' : '';
                        window.open(`${prefix}/${selectedCar.slug}/locations/${enrichedLocation.id}`, '_blank');
                      } : undefined}
                      className={isReadOnly ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5 active:bg-gray-100 dark:active:bg-white/10 transition-colors' : ''}
                      suffix={!isReadOnly && (
                        <div className="flex items-center self-stretch gap-1 ml-1 pl-3 border-l border-gray-100 dark:border-white/10">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const prefix = isAdminView ? '/admin' : '';
                              window.open(`${prefix}/${selectedCar.slug}/locations/${enrichedLocation.id}`, '_blank');
                            }}
                            className="p-2 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-lg transition-all"
                          >
                            <ExternalLink size={18} />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleRemoveLocation();
                            }}
                            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-all"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      )}
                    />
                  </div>
                ) : (
                  !isReadOnly && (
                    <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm p-4 text-center">
                      <p className="text-sm text-gray-400 dark:text-gray-600 italic">
                        {(t.locations as any)?.noLocations || 'No location selected'}
                      </p>
                    </div>
                  )
                )}
              </div>
            );
          })()}

          <div>
            <div className="flex items-center justify-between mb-4 px-1">
              <span className={theme.typography.sectionTitle}>
                {t.expenses.carData}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 font-primary">{t.expenses.carValue} ({effectivePreferences?.currency_symbol || '€'})</label>
                <input type="number" step="0.01" disabled={isReadOnly} value={valueAtExpense} onChange={(e) => handleValueChange(e.target.value)} min="0" className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-primary disabled:bg-gray-50 dark:disabled:bg-slate-900/20 disabled:text-gray-500" placeholder="0.00" />
                {!isReadOnly && <p className="text-xs text-gray-400 mt-2 ml-1">{t.expenses.carValueDescription}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 font-primary">{t.expenses.mileage} ({effectivePreferences?.distance_unit === 'miles' ? t.common.units.miles : t.common.units.kilometers})</label>
                <input type="number" disabled={isReadOnly} value={mileageAtExpense} onChange={(e) => setMileageAtExpense(e.target.value)} min="0" className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-primary disabled:bg-gray-50 dark:disabled:bg-slate-900/20 disabled:text-gray-500" placeholder="0" />
              </div>
            </div>
          </div>

          {!isReadOnly && (
            <div className="pt-2">
              <div className="flex items-center justify-between mb-2 px-1">
                <span className={theme.typography.sectionTitle}>
                  {t.repeatingExpenses.title}
                </span>
                {!isReadOnly && (
                  <IOSSwitch
                    checked={isRepeating}
                    onChange={(val) => {
                      if (!val && logEntry?.repeat_id) {
                        setShowDeleteRepeatModal(true);
                      } else {
                        setIsRepeating(val);
                        if (!val) setRepeatEndDate(null);
                      }
                    }}
                    disabled={isReadOnly}
                  />
                )}
              </div>

              {!isRepeating && !isReadOnly && (
                <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm p-4 text-center">
                  <p className="text-sm text-gray-400 dark:text-gray-600 italic">
                    {t.repeatingExpenses.placeholder}
                  </p>
                </div>
              )}

              {isRepeating && (
                <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm p-4 space-y-6 animate-in fade-in slide-in-from-top-2 duration-300">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-500/10 rounded-lg">
                        <Repeat className="text-blue-500" size={20} />
                      </div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t.repeatingExpenses.monthly}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t.repeatingExpenses.endDate}
                      </label>
                      <IOSSwitch 
                        checked={hasEndDate} 
                        onChange={(val) => {
                          setHasEndDate(val);
                          if (!val) setRepeatEndDate(null);
                        }} 
                        disabled={isReadOnly} 
                      />
                    </div>

                    {hasEndDate && (
                      <div className="relative group">
                        <input
                          type="date"
                          value={repeatEndDate || ''}
                          onChange={(e) => setRepeatEndDate(e.target.value || null)}
                          disabled={isReadOnly}
                          className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-white/5 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 transition-all font-primary"
                        />
                      </div>
                    )}
                  </div>

                  {(title.trim() || selectedLocationObj || lineItems.some(item => item.description?.trim())) && (
                    <div className="space-y-4 pt-4 border-t border-gray-100 dark:border-white/5">
                      <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        {t.repeatingExpenses.copyOptions}
                      </label>
                      
                      {title.trim() && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">{t.repeatingExpenses.copyTitle}</span>
                          <IOSSwitch checked={repeatCopyTitle} onChange={setRepeatCopyTitle} disabled={isReadOnly} />
                        </div>
                      )}
                      
                      {selectedLocationObj && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">{t.repeatingExpenses.copyLocation}</span>
                          <IOSSwitch checked={repeatCopyLocation} onChange={setRepeatCopyLocation} disabled={isReadOnly} />
                        </div>
                      )}
                      
                      {lineItems.some(item => item.description?.trim()) && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">{t.repeatingExpenses.copyDescription}</span>
                          <IOSSwitch checked={repeatCopyDescription} onChange={setRepeatCopyDescription} disabled={isReadOnly} />
                        </div>
                      )}
                    </div>
                  )}

                  <div className="bg-blue-50 dark:bg-blue-500/5 rounded-2xl p-4 space-y-2">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-semibold text-blue-600 dark:text-blue-400 mr-2">{t.repeatingExpenses.nextDate}:</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {(() => {
                          const nextDate = calculateNextRepeatDate(date, repeatEndDate);
                          return nextDate ? formatDateDisplay(nextDate, currentLanguage) : '-';
                        })()}
                      </span>
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-semibold text-blue-600 dark:text-blue-400 mr-2">{t.repeatingExpenses.until}:</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {(() => {
                          const nextDate = calculateNextRepeatDate(date, repeatEndDate);
                          if (!nextDate && repeatEndDate) return '-';
                          return repeatEndDate 
                            ? formatDateDisplay(repeatEndDate, currentLanguage)
                            : t.repeatingExpenses.noTerm;
                        })()}
                      </span>
                    </p>
                    
                    {isAdminView && logEntry?.repeat_id && (
                      <button
                        type="button"
                        onClick={handleSimulateRepeat}
                        disabled={isSubmitting}
                        className="mt-4 w-full py-3 bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-xl text-sm font-medium hover:bg-blue-500/20 transition-all flex items-center justify-center gap-2 border border-blue-500/20"
                      >
                        <Play size={16} fill="currentColor" />
                        {t.repeatingExpenses.simulate}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {isRepeating && relatedEntries.length > 0 && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setIsRelatedEntriesExpanded(!isRelatedEntriesExpanded)}
                    className="flex items-center gap-2 text-sm font-medium text-blue-500 hover:text-blue-600 transition-colors mb-2 px-1"
                  >
                    {isRelatedEntriesExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    {t.repeatingExpenses.relatedEntries} ({relatedEntries.length})
                  </button>

                  {isRelatedEntriesExpanded && (
                    <div className="space-y-2">
                      {relatedEntries.map(relatedEntry => (
                        <div key={relatedEntry.id} className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm">
                          <LogEntryItem
                            entry={relatedEntry}
                            slug={selectedCar?.slug}
                            effectivePreferences={effectivePreferences}
                            openInNewTab
                            isAdminView={isAdminView}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {!isReadOnly && mode === 'edit' && logEntry && (
            <div className="pt-4">
              <div className="mb-2 px-1">
                <span className={theme.typography.sectionTitle}>{t.common.additionalOptions}</span>
              </div>
              <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm divide-y divide-gray-100 dark:divide-white/5">
                <button
                  type="button"
                  onClick={handleDelete}
                  className="w-full text-left p-4 text-base font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors flex items-center justify-between"
                >
                  {t.expenses.deleteExpense}
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          )}
        </form>
        <div className="h-[40vh]" />
      </div>



      <ConfirmModal isOpen={showConfirmModal} title={t.common.unsavedChangesTitle} message={t.common.unsavedChangesMessage} confirmLabel={t.common.save} cancelLabel={t.common.dontSave} onConfirm={() => { handleSubmit(); setShowConfirmModal(false); }} onCancel={() => { setShowConfirmModal(false); const unsavedUrls = images.map(img => img.url).filter(url => url && !savedImageUrlsRef.current.has(url)); if (unsavedUrls.length > 0) deleteTempFiles(unsavedUrls); onClose(); }} onClose={() => setShowConfirmModal(false)} isDestructive={false} />
      <ConfirmModal isOpen={showDeleteModal} title={t.expenses.deleteExpense} message={t.expenses.deleteConfirm} confirmLabel={t.common.delete} cancelLabel={t.common.cancel} onConfirm={handleDeleteCall} onCancel={() => setShowDeleteModal(false)} onClose={() => setShowDeleteModal(false)} isDestructive={true} />
      <ConfirmModal isOpen={showDeleteRepeatModal} title={t.repeatingExpenses.deleteRepeat} message={t.repeatingExpenses.deleteRepeatConfirm} confirmLabel={t.common.delete} cancelLabel={t.common.cancel} onConfirm={handleDeleteRepeatCall} onCancel={() => setShowDeleteRepeatModal(false)} onClose={() => setShowDeleteRepeatModal(false)} isDestructive={true} />
      <AlertModal isOpen={alertConfig.isOpen} title={alertConfig.title} message={alertConfig.message} confirmLabel={t.common.ok} onClose={() => setAlertConfig({ isOpen: false, title: '', message: '' })} />

      <LocationSelectorModal
        isOpen={isLocationModalOpen}
        carId={carId}
        onClose={() => setIsLocationModalOpen(false)}
        onSelect={(loc) => {
          setLocationId(loc.id);
          setSelectedLocationObj(loc);
          setIsLocationModalOpen(false);
        }}
        onAddNew={() => {
          setIsLocationModalOpen(false);
          setIsLocationCreateModalOpen(true);
        }}
        expenseType={lineItems.length > 0 && lineItems[0].type ? lineItems[0].type : null}
      />

      {isLocationCreateModalOpen && (
        <LocationForm
          carId={carId}
          onCreated={(newLoc: any) => {
            setLocationId(newLoc.id);
            setSelectedLocationObj(newLoc);
          }}
          onClose={() => {
            setIsLocationCreateModalOpen(false);
            queryClient.invalidateQueries({ queryKey: LOCATION_KEYS.list(carId) });
          }}
        />
      )}
    </div>
  );
}

const formatDateDisplay = (dateStr: string, lang: string) => {
  if (!dateStr || dateStr === '—') return '—';
  try {
    const pureDate = dateStr.split('T')[0];
    const parts = pureDate.split(/[-/.]/);
    if (parts.length !== 3) return dateStr;
    const [y, m, d] = parts.map(Number);
    const dateObj = new Date(y, m - 1, d);
    if (isNaN(dateObj.getTime())) return '—';
    return dateObj.toLocaleDateString(lang, { day: '2-digit', month: 'long', year: 'numeric' });
  } catch (e) {
    return '—';
  }
};

const IOSSwitch = ({ checked, onChange, disabled }: { checked: boolean, onChange: (val: boolean) => void, disabled?: boolean }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={(e) => {
      e.stopPropagation();
      onChange(!checked);
    }}
    className={`
      relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent 
      transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2
      ${checked ? 'bg-blue-600' : 'bg-gray-200 dark:bg-slate-700'}
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
    `}
  >
    <span
      className={`
        pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
        transition duration-200 ease-in-out
        ${checked ? 'translate-x-5' : 'translate-x-0'}
      `}
    />
  </button>
);
